import { useMemo } from "react";
import { resultFromWire, type RunResult } from "@/api/adapt";
import { api, qs } from "@/api/http";
import { useQuery } from "@/api/query";
import type {
	ChangesetDetailDto,
	ChangesetListDto,
	CompareDto,
	TemplateInfoDto,
	DriversDto,
	ForecastHistoryDto,
	HomeDto,
	NodeDetailDto,
	NodeResultsDto,
	ResultsDto,
	ScenarioSummaryDto,
	SearchDto,
	WhatMattersDto,
	Zoom,
} from "@/api/types";
import { useStore } from "@/model/store";

// Reads from the API, keyed on the worldview revision: a write or a revision event re-keys them all,
// and each keeps its previous data on screen while the new one loads.

function useKey(path: string | null): string | null {
	const ws = useStore((s) => s.ws);
	const rev = useStore((s) => s.revision);
	if (!path || !ws || rev === null) return null;
	return `/w/${encodeURIComponent(ws)}${path}#${rev}`;
}

/** GET the path part of a query key (the `#revision` suffix only re-keys it). */
function get<T>(key: string | null) {
	return () => api.get<T>(key!.slice(0, key!.lastIndexOf("#")));
}

/** A whole run: Baseline (`null`) or a scenario. Charts, tables, graph badges, Home sparks. */
export function useRun(scenarioId: string | null | undefined): { data: RunResult | null; pending: boolean } {
	const key = useKey(`/results${qs({ scenario: scenarioId ?? null })}`);
	const q = useQuery(key, async () => resultFromWire(await get<ResultsDto>(key)()));
	return { data: q.data, pending: q.pending };
}

export function useBase() {
	return useRun(null);
}

/** The scenario being viewed and edited across the app (null = Baseline). */
export function useActiveScenario() {
	const id = useStore((s) => s.activeScenario);
	const scenario = useStore((s) => s.world.scenarios.find((x) => x.id === id) ?? null);
	return { scenario };
}

/** A scenario's run; Baseline's when the scenario sets nothing (the API would return the same run). */
export function useScenarioRun(scenarioId: string | null | undefined) {
	const levers = useStore((s) => (scenarioId ? (s.world.scenarios.find((x) => x.id === scenarioId)?.levers.length ?? 0) : 0));
	return useRun(scenarioId && levers ? scenarioId : null);
}

/**
 * What the app should show right now: the active scenario's results (or Baseline's when none
 * is active), with Baseline's alongside for "was …" comparisons.
 */
export function useActiveScenarioResult() {
	const { scenario } = useActiveScenario();
	const base = useBase();
	const scen = useScenarioRun(scenario?.id);
	return {
		scenario,
		base: base.data,
		result: scenario ? scen.data : base.data,
		pending: base.pending || (!!scenario && scen.pending),
	};
}

/**
 * Definition, stated series, bars, market ladder and context of one node (`GET /nodes/:h`). With a
 * scenario that sets this node's own curve or range, it also has the scenario's series (`scenario_series`).
 */
export function useNodeDetail(handle: string | null, zoom: Zoom = "year", scenarioId: string | null = null) {
	const key = useKey(handle ? `/nodes/${encodeURIComponent(handle)}${qs({ zoom, scenario: scenarioId })}` : null);
	return useQuery(key, get<NodeDetailDto>(key));
}

/** One node's model (and without the scenario) with bars at `zoom` (`GET /nodes/:h/results`). */
export function useNodeResults(handle: string | null, scenarioId: string | null | undefined, zoom: Zoom = "year") {
	const key = useKey(handle ? `/nodes/${encodeURIComponent(handle)}/results${qs({ scenario: scenarioId ?? null, zoom })}` : null);
	return useQuery(key, get<NodeResultsDto>(key));
}

export function useWhatMatters(handle: string | null, date: string, scenarioId: string | null | undefined) {
	const key = useKey(handle ? `/nodes/${encodeURIComponent(handle)}/what-matters${qs({ date, scenario: scenarioId ?? null })}` : null);
	return useQuery(key, get<WhatMattersDto>(key));
}

export function useScenarioDrivers(scenarioId: string | null, node: string | null, date?: string) {
	const key = useKey(scenarioId && node ? `/scenarios/${encodeURIComponent(scenarioId)}/drivers${qs({ node, date })}` : null);
	return useQuery(key, get<DriversDto>(key));
}

export function useScenarioSummary(scenarioId: string | null) {
	const key = useKey(scenarioId ? `/scenarios/${encodeURIComponent(scenarioId)}` : null);
	return useQuery(key, get<ScenarioSummaryDto>(key));
}

/** Baseline plus every scenario, as rows of nodes (`GET /compare`). */
export function useCompare(focus?: string | null, enabled = true) {
	const key = useKey(enabled ? `/compare${qs({ focus: focus ?? null })}` : null);
	return useQuery(key, get<CompareDto>(key));
}

export function useHome(scenarioId: string | null | undefined) {
	const key = useKey(`/home${qs({ scenario: scenarioId ?? null })}`);
	return useQuery(key, get<HomeDto>(key));
}

export function useSearch(q: string, scenarioId: string | null | undefined) {
	const trimmed = q.trim();
	const key = useKey(trimmed ? `/search${qs({ q: trimmed, scenario: scenarioId ?? null, limit: 50 })}` : null);
	return useQuery(key, get<SearchDto>(key));
}

export function useForecastHistory(handle: string) {
	const key = useKey(`/nodes/${encodeURIComponent(handle)}/forecast/history`);
	return useQuery(key, get<ForecastHistoryDto>(key));
}

/** Scenario cards (chance and biggest change for each), from `/home`. */
export function useScenarioCards() {
	const active = useStore((s) => s.activeScenario);
	const home = useHome(active);
	return useMemo(() => ({ data: home.data?.scenarios ?? null, pending: home.pending }), [home.data, home.pending]);
}

/** The activity log, newest first: one page of change sets (`GET /changesets`). `before` is a revision cursor. */
/** `origin` is one origin or a comma-separated list (`mcp,api`). */
export function useChangesets(origin: string | null, before: number | null = null, limit = 50) {
	const key = useKey(`/changesets${qs({ origin, before, limit })}`);
	return useQuery(key, get<ChangesetListDto>(key));
}

/** One change set with each change as a sentence (`GET /changesets/:id`). */
export function useChangeset(id: string | null) {
	const key = useKey(id ? `/changesets/${encodeURIComponent(id)}` : null);
	return useQuery(key, get<ChangesetDetailDto>(key));
}

/** The templates a workspace can start from (`GET /templates`). */
export function useTemplates(enabled = true) {
	const key = useKey(enabled ? "/templates" : null);
	return useQuery(key, get<{ templates: TemplateInfoDto[] }>(key));
}
